import {
  collection,
  getDocs,
  limit,
  query,
  where,
  type QueryConstraint,
} from "firebase/firestore";

import { db } from "@/lib/firebase";
import type { BusinessSearchDoc } from "@/lib/search/businessSearch";
import { normalizeText } from "@/lib/search/businessSearch";
import {
  buildDirectorySearchExpansion,
  type DirectorySearchExpansion,
} from "@/lib/search/searchExpansion";

export type SearchLocationFilter = {
  countyId?: string | null;
  cityId?: string | null;
  areaId?: string | null;
};

export type SearchBusinessesParams = {
  queryText: string;
  location?: SearchLocationFilter;
  limitCount?: number;
};

const BUSINESS_SEARCH_COLLECTION = "businessSearch";

const DEFAULT_LIMIT_COUNT = 30;
const QUERY_FETCH_LIMIT = 50;
const MAX_EXPANDED_RANKING_TERMS = 24;

const ACTIVE_SUBSCRIPTION_STATUSES = new Set([
  "",
  "active",
  "trialing",
  "paid",
  "approved",
  "enabled",
]);

function cleanString(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim();
}

function uniqueStrings(values: (string | null | undefined)[]): string[] {
  return Array.from(
    new Set(values.map((v) => (v ?? "").trim()).filter(Boolean))
  );
}

function tokenizePhrase(value: string): string[] {
  const normalized = normalizeText(value);
  if (!normalized) return [];

  return normalized.split(" ").filter(Boolean);
}

function normalizeSubscriptionStatus(value: unknown): string {
  if (typeof value !== "string") return "";
  return value.trim().toLowerCase();
}

function hasActiveSubscriptionForSearch(doc: BusinessSearchDoc): boolean {
  /**
   * If subscriptionStatus is missing, allow it for now.
   *
   * This keeps old Pioneer / existing approved businesses visible
   * until you fully add subscriptionStatus to every businessSearch doc.
   */
  const status = normalizeSubscriptionStatus((doc as any).subscriptionStatus);

  return ACTIVE_SUBSCRIPTION_STATUSES.has(status);
}

function buildLocationConstraints(
  location?: SearchLocationFilter
): QueryConstraint[] {
  const constraints: QueryConstraint[] = [];

  /**
   * Priority:
   * 1. Area
   * 2. City
   * 3. County
   *
   * If areaId is selected, we do not also add cityId/countyId.
   * The area already belongs inside a city/county.
   */
  if (location?.areaId) {
    constraints.push(where("areaId", "==", location.areaId));
  } else if (location?.cityId) {
    constraints.push(where("cityId", "==", location.cityId));
  } else if (location?.countyId) {
    constraints.push(where("countyId", "==", location.countyId));
  }

  return constraints;
}

/**
 * Compatibility helper:
 * - New businessSearch docs write searchWords and searchTokens.
 * - Older docs may only have one of them.
 *
 * Keep search working for both.
 */
function getSearchTerms(doc: BusinessSearchDoc): string[] {
  return uniqueStrings([
    ...((doc as any).tokens ?? []),
    ...(doc.searchWords ?? []),
    ...(doc.searchTokens ?? []),
    ...((doc as any).phraseTokens ?? []),
    ...((doc as any).itemTokens ?? []),
    ...((doc as any).locationTokens ?? []),
    ...((doc as any).categoryTokens ?? []),
  ]);
}

function getScopedTerms(
  doc: BusinessSearchDoc,
  field:
    | "phraseTokens"
    | "itemTokens"
    | "locationTokens"
    | "categoryTokens"
): string[] {
  const value = (doc as any)[field];
  return Array.isArray(value) ? value.map(String).map(normalizeText) : [];
}

/**
 * Extra safety check after Firestore returns results.
 *
 * Firestore query already filters most public fields, but this protects the UI
 * if a stale/wrong document somehow slips through.
 */
function isPublicApprovedBusiness(doc: BusinessSearchDoc): boolean {
  return (
    doc.status === "approved" &&
    doc.isDeleted === false &&
    doc.isPublished === true &&
    (doc as any).isDeactivated !== true &&
    hasActiveSubscriptionForSearch(doc)
  );
}

function buildHaystack(doc: BusinessSearchDoc): string {
  return uniqueStrings([
    doc.businessNameLower,
    doc.categoryNameLower,
    doc.subcategoryNameLower,
    doc.countyName ? normalizeText(doc.countyName) : "",
    doc.cityName ? normalizeText(doc.cityName) : "",
    doc.areaName ? normalizeText(doc.areaName) : "",
    doc.shortDescription ? normalizeText(doc.shortDescription) : "",
    (doc as any).searchableText ?? "",
    ...getSearchTerms(doc),
    doc.searchText ?? "",
  ]).join(" ");
}

function textMatchesTerm(text: string, textWords: Set<string>, term: string) {
  const cleanTerm = normalizeText(term);
  if (!cleanTerm) return false;
  if (text.includes(cleanTerm)) return true;

  const termTokens = tokenizePhrase(cleanTerm);
  return termTokens.length > 0 && termTokens.every((token) => textWords.has(token));
}

function matchesExpandedCandidate(
  doc: BusinessSearchDoc,
  expansion: DirectorySearchExpansion
): boolean {
  if (expansion.candidateTerms.length === 0) return false;

  const haystack = buildHaystack(doc);
  const haystackWords = new Set(tokenizePhrase(haystack));

  return expansion.candidateTerms.some((term) =>
    textMatchesTerm(haystack, haystackWords, term)
  );
}

function sumTopScores(scores: number[], maxCount: number): number {
  return scores
    .filter((score) => score > 0)
    .sort((a, b) => b - a)
    .slice(0, maxCount)
    .reduce((total, score) => total + score, 0);
}

function scoreBusiness(
  doc: BusinessSearchDoc,
  normalizedQuery: string,
  expansion?: DirectorySearchExpansion
): number {
  const words = getSearchTerms(doc);
  const wordSet = new Set(words);
  const phraseSet = new Set(getScopedTerms(doc, "phraseTokens"));
  const itemSet = new Set(getScopedTerms(doc, "itemTokens"));
  const locationSet = new Set(getScopedTerms(doc, "locationTokens"));
  const categorySet = new Set(getScopedTerms(doc, "categoryTokens"));
  const text = doc.searchText ?? "";
  const searchableText = normalizeText((doc as any).searchableText ?? "");
  const serviceText = uniqueStrings([
    text,
    searchableText,
    doc.shortDescription ? normalizeText(doc.shortDescription) : "",
  ]).join(" ");

  const name =
    doc.businessNameLower ?? normalizeText(cleanString(doc.businessName));

  const category =
    doc.categoryNameLower ?? normalizeText(cleanString(doc.categoryName));

  const subcategory =
    doc.subcategoryNameLower ??
    normalizeText(cleanString(doc.subcategoryName));

  const queryTokens = tokenizePhrase(normalizedQuery);
  const haystack = buildHaystack(doc);
  const haystackWords = new Set(tokenizePhrase(haystack));
  const serviceWords = new Set(tokenizePhrase(serviceText));
  const nameWords = new Set(tokenizePhrase(name));
  const categoryText = [category, subcategory].filter(Boolean).join(" ");
  const categoryWords = new Set(tokenizePhrase(categoryText));
  const locationText = uniqueStrings([
    doc.areaName ? normalizeText(doc.areaName) : "",
    doc.cityName ? normalizeText(doc.cityName) : "",
    doc.countyName ? normalizeText(doc.countyName) : "",
  ]).join(" ");

  let score = 0;

  /**
   * Strongest matches.
   */
  if (name === normalizedQuery) score += 220;
  if (name.startsWith(normalizedQuery)) score += 140;
  if (name.includes(normalizedQuery)) score += 100;

  /**
   * Phrase/text matches.
   */
  if (itemSet.has(normalizedQuery)) score += 180;
  if (phraseSet.has(normalizedQuery)) score += 125;
  if (categorySet.has(normalizedQuery)) score += 70;
  if (locationSet.has(normalizedQuery)) score += 50;

  if (text.includes(normalizedQuery)) score += 65;
  if (searchableText.includes(normalizedQuery)) score += 45;

  if (category === normalizedQuery) score += 60;
  if (subcategory === normalizedQuery) score += 60;

  if (category.includes(normalizedQuery)) score += 45;
  if (subcategory.includes(normalizedQuery)) score += 45;

  /**
   * Individual token matches.
   *
   * Example:
   * Query: "car wash"
   * Tokens: "car", "wash"
   */
  for (const token of queryTokens) {
    if (wordSet.has(token)) score += 25;
    if (itemSet.has(token)) score += 18;
    if (categorySet.has(token)) score += 12;
    if (locationSet.has(token)) score += 9;

    if (name.startsWith(token)) score += 20;
    if (name.includes(token)) score += 12;

    if (category.includes(token)) score += 8;
    if (subcategory.includes(token)) score += 8;

    if (text.includes(token)) score += 5;
  }

  if (expansion) {
    const scoreExpandedTerm = (
      term: string,
      scores: {
        itemToken: number;
        itemText: number;
        name: number;
        category: number;
        description: number;
        fallback: number;
      }
    ) => {
      const cleanTerm = normalizeText(term);
      if (!cleanTerm) return 0;

      if (itemSet.has(cleanTerm)) return scores.itemToken;
      if (textMatchesTerm(serviceText, serviceWords, cleanTerm)) {
        return scores.itemText;
      }
      if (textMatchesTerm(name, nameWords, cleanTerm)) return scores.name;
      if (categorySet.has(cleanTerm) || textMatchesTerm(categoryText, categoryWords, cleanTerm)) {
        return scores.category;
      }
      if (text.includes(cleanTerm)) return scores.description;
      if (wordSet.has(cleanTerm) || textMatchesTerm(haystack, haystackWords, cleanTerm)) {
        return scores.fallback;
      }

      return 0;
    };

    const phraseScores = expansion.phraseAliases
      .slice(0, MAX_EXPANDED_RANKING_TERMS)
      .map((term) =>
        scoreExpandedTerm(term, {
          itemToken: 160,
          itemText: 70,
          name: 110,
          category: 90,
          description: 58,
          fallback: 26,
        })
      );
    const coreScores = expansion.candidateTerms
      .slice(0, MAX_EXPANDED_RANKING_TERMS)
      .map((term) =>
        scoreExpandedTerm(term, {
          itemToken: 130,
          itemText: 58,
          name: 92,
          category: 76,
          description: 42,
          fallback: 18,
        })
      );
    const boostScores = expansion.boostTerms.slice(0, 12).map((term) =>
      scoreExpandedTerm(term, {
        itemToken: 18,
        itemText: 12,
        name: 10,
        category: 8,
        description: 5,
        fallback: 3,
      })
    );

    const locationScores = expansion.locationTerms.slice(0, 12).map((term) => {
      const cleanTerm = normalizeText(term);
      if (!cleanTerm) return 0;

      if (locationSet.has(cleanTerm) || locationText.includes(cleanTerm)) {
        return 200;
      }

      if (textMatchesTerm(haystack, haystackWords, cleanTerm)) return 8;

      return 0;
    });
    const locationAliasScores = expansion.locationAliases.slice(0, 8).map((term) => {
      const cleanTerm = normalizeText(term);
      if (!cleanTerm) return 0;

      if (locationSet.has(cleanTerm) || locationText.includes(cleanTerm)) {
        return 18;
      }

      if (textMatchesTerm(haystack, haystackWords, cleanTerm)) return 4;

      return 0;
    });

    score += sumTopScores(phraseScores, 4);
    score += sumTopScores(coreScores, 6);
    score += sumTopScores(boostScores, 4);
    score += sumTopScores(locationScores, 3);
    score += sumTopScores(locationAliasScores, 2);
  }

  /**
   * Small ranking boosts.
   */
  if (doc.isFeatured === true) score += 10;
  if (doc.isVerified === true) score += 5;
  if ((doc as any).hasLogo === true) score += 2;
  if ((doc as any).hasProfileImage === true) score += 2;
  if ((doc as any).hasLocationPin === true) score += 2;
  if ((doc as any).hasWhatsApp === true) score += 1;

  /**
   * Featured rank:
   * Lower rank means better placement.
   */
  if (doc.isFeatured === true && typeof doc.featuredRank === "number") {
    score += Math.max(0, 10 - Math.min(doc.featuredRank, 10));
  }

  return score;
}

function matchesAllTokens(
  doc: BusinessSearchDoc,
  normalizedQuery: string
): boolean {
  const tokens = tokenizePhrase(normalizedQuery);
  if (!tokens.length) return false;

  const haystackWords = new Set(tokenizePhrase(buildHaystack(doc)));

  return tokens.every((token) => haystackWords.has(token));
}

function getTopRankingReasons(
  doc: BusinessSearchDoc,
  normalizedQuery: string,
  expansion: DirectorySearchExpansion
): string[] {
  const name = doc.businessNameLower ?? normalizeText(cleanString(doc.businessName));
  const category = doc.categoryNameLower ?? normalizeText(cleanString(doc.categoryName));
  const subcategory =
    doc.subcategoryNameLower ?? normalizeText(cleanString(doc.subcategoryName));
  const text = uniqueStrings([
    doc.searchText ?? "",
    normalizeText((doc as any).searchableText ?? ""),
    doc.shortDescription ? normalizeText(doc.shortDescription) : "",
  ]).join(" ");
  const locationText = uniqueStrings([
    doc.areaName ? normalizeText(doc.areaName) : "",
    doc.cityName ? normalizeText(doc.cityName) : "",
    doc.countyName ? normalizeText(doc.countyName) : "",
  ]).join(" ");
  const haystack = buildHaystack(doc);
  const haystackWords = new Set(tokenizePhrase(haystack));
  const reasons: string[] = [];

  if (name === normalizedQuery) reasons.push("business name exact match");
  else if (name.startsWith(normalizedQuery)) {
    reasons.push("business name prefix match");
  } else if (name.includes(normalizedQuery)) {
    reasons.push("business name text match");
  }

  if (category.includes(normalizedQuery) || subcategory.includes(normalizedQuery)) {
    reasons.push("category/subcategory match");
  }

  if (text.includes(normalizedQuery)) {
    reasons.push("description or item text match");
  }

  for (const term of expansion.phraseAliases.slice(0, 8)) {
    if (textMatchesTerm(text, new Set(tokenizePhrase(text)), term)) {
      reasons.push(`service/item phrase alias: ${term}`);
    } else if (
      textMatchesTerm(
        [category, subcategory].filter(Boolean).join(" "),
        new Set(tokenizePhrase([category, subcategory].join(" "))),
        term
      )
    ) {
      reasons.push(`category phrase alias: ${term}`);
    } else if (textMatchesTerm(name, new Set(tokenizePhrase(name)), term)) {
      reasons.push(`name phrase alias: ${term}`);
    } else if (textMatchesTerm(haystack, haystackWords, term)) {
      reasons.push(`fallback phrase alias: ${term}`);
    }

    if (reasons.length >= 5) break;
  }

  for (const term of expansion.candidateTerms.slice(0, 12)) {
    if (textMatchesTerm(text, new Set(tokenizePhrase(text)), term)) {
      reasons.push(`service/item candidate: ${term}`);
    } else if (
      textMatchesTerm(
        [category, subcategory].filter(Boolean).join(" "),
        new Set(tokenizePhrase([category, subcategory].join(" "))),
        term
      )
    ) {
      reasons.push(`category candidate: ${term}`);
    } else if (textMatchesTerm(name, new Set(tokenizePhrase(name)), term)) {
      reasons.push(`name candidate: ${term}`);
    } else if (textMatchesTerm(haystack, haystackWords, term)) {
      reasons.push(`fallback candidate: ${term}`);
    }

    if (reasons.length >= 5) break;
  }

  for (const term of expansion.boostTerms.slice(0, 8)) {
    if (textMatchesTerm(haystack, haystackWords, term)) {
      reasons.push(`boost-only alias: ${term}`);
    }

    if (reasons.length >= 6) break;
  }

  for (const term of expansion.locationTerms.slice(0, 6)) {
    if (textMatchesTerm(locationText, new Set(tokenizePhrase(locationText)), term)) {
      reasons.push(`location boost: ${term}`);
    }

    if (reasons.length >= 6) break;
  }

  for (const term of expansion.locationAliases.slice(0, 4)) {
    if (textMatchesTerm(locationText, new Set(tokenizePhrase(locationText)), term)) {
      reasons.push(`location alias boost: ${term}`);
    }

    if (reasons.length >= 6) break;
  }

  if (doc.isFeatured === true) reasons.push("featured boost");
  if (doc.isVerified === true) reasons.push("verified boost");

  return uniqueStrings(reasons).slice(0, 6);
}

function logSearchDebug(options: {
  originalQuery: string;
  expansion: DirectorySearchExpansion;
  candidateCount: number;
  ranked: BusinessSearchDoc[];
}) {
  if (!__DEV__) return;

  const normalizedQuery = normalizeText(options.originalQuery);
  if (!normalizedQuery) return;

  console.log("Business search ranking completed.", {
    candidateCount: options.candidateCount,
    rankedCount: options.ranked.length,
    topRankingMetrics: options.ranked.slice(0, 5).map((doc) => ({
      score: scoreBusiness(doc, normalizedQuery, options.expansion),
      reasonCount: getTopRankingReasons(
        doc,
        normalizedQuery,
        options.expansion
      ).length,
    })),
  });
}

function dedupeBusinesses(rows: BusinessSearchDoc[]): BusinessSearchDoc[] {
  const seen = new Set<string>();
  const out: BusinessSearchDoc[] = [];

  for (const row of rows) {
    const key = row.businessId;
    if (!key || seen.has(key)) continue;

    seen.add(key);
    out.push(row);
  }

  return out;
}

function buildQueryPrefixes(
  normalizedQuery: string,
  expansion?: DirectorySearchExpansion
): string[] {
  const tokens = tokenizePhrase(normalizedQuery);

  /**
   * Query several possible prefixes so multi-word search works.
   *
   * Example:
   * "car wash"
   *
   * Query:
   * - "car wash"
   * - "car"
   * - "wash"
   *
   * Then filter client-side to make sure all tokens are present.
   */
  const sourceTerms = uniqueStrings([
    ...tokens,
    ...(expansion?.prefixTerms ?? []),
    ...(expansion?.candidateTerms ?? []),
    ...(expansion?.phraseAliases ?? []),
    ...(expansion?.anchorTerms ?? []),
    ...(expansion?.locationTerms ?? []),
    normalizedQuery,
  ]);

  return uniqueStrings(
    sourceTerms
      .flatMap((term) => tokenizePhrase(term))
      .filter((term) => !term.includes(" "))
  ).slice(0, 10);
}

function matchesSearchCandidate(
  doc: BusinessSearchDoc,
  normalizedQuery: string,
  expansion: DirectorySearchExpansion
): boolean {
  return (
    matchesAllTokens(doc, normalizedQuery) ||
    matchesExpandedCandidate(doc, expansion)
  );
}

async function runPrefixQuery(
  prefix: string,
  location?: SearchLocationFilter,
  maxRows = QUERY_FETCH_LIMIT
): Promise<BusinessSearchDoc[]> {
  const cleanPrefix = normalizeText(prefix);

  if (!cleanPrefix) return [];

  const constraints: QueryConstraint[] = [
    /**
     * Critical public visibility filters.
     *
     * These must match businessSearch/{businessId}.
     */
    where("status", "==", "approved"),
    where("isDeleted", "==", false),
    where("isPublished", "==", true),
    where("isDeactivated", "==", false),

    /**
     * Search index field.
     */
    where("searchPrefixes", "array-contains", cleanPrefix),

    /**
     * Optional location filter.
     */
    ...buildLocationConstraints(location),

    limit(maxRows),
  ];

  const ref = query(collection(db, BUSINESS_SEARCH_COLLECTION), ...constraints);

  const snap = await getDocs(ref);

  return snap.docs.map((docSnap) => {
    const data = docSnap.data() as BusinessSearchDoc;

    return {
      ...data,
      businessId: data.businessId ?? docSnap.id,
      isDeactivated: (data as any).isDeactivated === true ? true : false,
    } as BusinessSearchDoc;
  });
}

export async function searchBusinesses({
  queryText,
  location,
  limitCount = DEFAULT_LIMIT_COUNT,
}: SearchBusinessesParams): Promise<BusinessSearchDoc[]> {
  const normalizedQuery = normalizeText(queryText);

  if (!normalizedQuery) return [];

  const tokens = tokenizePhrase(normalizedQuery);
  if (!tokens.length) return [];

  const expansion = buildDirectorySearchExpansion(queryText);
  const queryPrefixes = buildQueryPrefixes(normalizedQuery, expansion);

  const queryResults = await Promise.all(
    queryPrefixes.map((prefix) =>
      runPrefixQuery(prefix, location, QUERY_FETCH_LIMIT)
    )
  );

  const merged = dedupeBusinesses(queryResults.flat());

  const filtered = merged
    .filter(isPublicApprovedBusiness)
    .filter((doc) => matchesSearchCandidate(doc, normalizedQuery, expansion));

  const sorted = filtered
    .map((doc) => ({
      doc,
      score: scoreBusiness(doc, normalizedQuery, expansion),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;

      const aRank =
        typeof a.doc.featuredRank === "number" ? a.doc.featuredRank : 9999;

      const bRank =
        typeof b.doc.featuredRank === "number" ? b.doc.featuredRank : 9999;

      if (aRank !== bRank) return aRank - bRank;

      return cleanString(a.doc.businessName).localeCompare(
        cleanString(b.doc.businessName)
      );
    })
    .map((row) => row.doc);

  const limited = sorted.slice(0, limitCount);

  logSearchDebug({
    originalQuery: queryText,
    expansion,
    candidateCount: filtered.length,
    ranked: limited,
  });

  return limited;
}

export type LocalBusinessSearchRank = {
  doc: BusinessSearchDoc;
  score: number;
};

export function rankBusinessSearchDocsLocally(
  rows: BusinessSearchDoc[],
  queryText: string,
  limitCount = DEFAULT_LIMIT_COUNT
): LocalBusinessSearchRank[] {
  const normalizedQuery = normalizeText(queryText);
  if (!normalizedQuery) return [];

  const expansion = buildDirectorySearchExpansion(queryText);

  return rows
    .filter(isPublicApprovedBusiness)
    .filter((doc) => matchesSearchCandidate(doc, normalizedQuery, expansion))
    .map((doc) => ({
      doc,
      score: scoreBusiness(doc, normalizedQuery, expansion),
    }))
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;

      return cleanString(a.doc.businessName).localeCompare(
        cleanString(b.doc.businessName)
      );
    })
    .slice(0, limitCount);
}

export async function suggestBusinessesPrefix(
  rawPrefix: string,
  location?: SearchLocationFilter
): Promise<BusinessSearchDoc[]> {
  const normalizedPrefix = normalizeText(rawPrefix);

  if (!normalizedPrefix) return [];

  const rows = await runPrefixQuery(normalizedPrefix, location, 10);

  const sorted = rows
    .filter(isPublicApprovedBusiness)
    .map((doc) => ({
      doc,
      score: scoreBusiness(doc, normalizedPrefix),
    }))
    .sort((a, b) => b.score - a.score)
    .map((row) => row.doc);

  return dedupeBusinesses(sorted).slice(0, 10);
}
